import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Logout,
  Assignment,
  ShoppingCart,
  CalendarToday,
  TrendingUp,
  CheckCircle,
  Pending,
  FamilyRestroom,
  AutoAwesome,
  PhotoLibrary,
  PhotoCamera,
  ExpandMore,
  Delete,
  Warning,
  Security,
  Notifications,
  DoneAll,
  Chat,
  PhoneAndroid,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { suggestChores } from '../utils/aiHelper';
import InviteCodeCard from '../components/InviteCodeCard';
import TaskCard from '../components/TaskCard';
import CompletedTasksCard from '../components/CompletedTasksCard';
import UserProfile from '../components/UserProfile';
import FeedbackWidget from '../components/FeedbackWidget';
import ReviewPrompt from '../components/ReviewPrompt';
import BurtChatWidget from '../components/BurtChatWidget';
import RecipeManager from '../components/RecipeManager';
import AffiliateSuggestions from '../components/AffiliateSuggestions';
import PWASettings from '../components/PWASettings';

import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, collection, query, where, getDocs, getDoc, updateDoc, deleteDoc, onSnapshot, orderBy, limit, addDoc } from 'firebase/firestore';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import { requestNotificationPermission, areNotificationsEnabled } from '../utils/notificationHelper';
import { buildScreenTimeRule } from '../services/RuleScheduler';
import { Tooltip } from '@mui/material';

function ParentDashboard() {
  const { currentUser, userProfile, logout, fetchUserProfile } = useAuth();
  const { tasks, familyMembers, shoppingList, createTask, addShoppingItem } = useFamily();
  const { familyData, familyId } = useFamilyId();
  const navigate = useNavigate();
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openFamilyDialog, setOpenFamilyDialog] = useState(false);
  const [openAllowanceDialog, setOpenAllowanceDialog] = useState(false);
  const [payoutMember, setPayoutMember] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutProcessing, setPayoutProcessing] = useState(false);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [openPendingDialog, setOpenPendingDialog] = useState(false);
  const [openCompletedDialog, setOpenCompletedDialog] = useState(false);
  const [openUserProfile, setOpenUserProfile] = useState(false);
  const [profilePicUploading, setProfilePicUploading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [parentAlerts, setParentAlerts] = useState([]);
  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignedTo: [],
    allowance: '',  // Blank by default — user enters dollars/cents
    dueDate: '',
    startTime: '',
    endTime: '',
    photoRequired: 'none',
    isRecurring: false,
    recurringType: 'daily',
    recurringInterval: 1,
    screenTimeEnabled: false, // Lock device during chore
  });

  // Fix #1: Force scroll to top on mount to prevent auto-scroll from BurtChat
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer1 = setTimeout(() => window.scrollTo(0, 0), 50);
    const timer2 = setTimeout(() => window.scrollTo(0, 0), 100);
    const timer3 = setTimeout(() => window.scrollTo(0, 0), 200);
    const timer4 = setTimeout(() => window.scrollTo(0, 0), 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  // Fetch pending purchase requests from children's savings goals
  useEffect(() => {
    if (!familyMembers.length) return;
    const childIds = familyMembers.filter(m => m.role === 'child').map(m => m.id);
    if (!childIds.length) return;
    
    (async () => {
      try {
        const allRequests = [];
        for (const childUid of childIds) {
          const goalsQuery = query(
            collection(db, 'savingsGoals'),
            where('userId', '==', childUid),
            where('status', '==', 'pending_approval')
          );
          const snap = await getDocs(goalsQuery);
          const child = familyMembers.find(m => m.id === childUid);
          snap.docs.forEach(d => {
            allRequests.push({
              id: d.id,
              childName: child?.displayName || 'Unknown',
              ...d.data()
            });
          });
        }
        setPurchaseRequests(allRequests);
      } catch (err) {
        console.warn('Error loading purchase requests:', err);
      }
    })();
  }, [familyMembers]);

  // Subscribe to safety alerts for this family
  useEffect(() => {
    if (!familyId) return;
    const alertsQuery = query(
      collection(db, 'families', familyId, 'safety_alerts'),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    const unsubscribe = onSnapshot(alertsQuery, (snap) => {
      const alerts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setParentAlerts(alerts);
    }, (err) => {
      console.warn('Safety alerts listener error:', err);
    });
    return () => unsubscribe();
  }, [familyId]);

  // Check notification permission on mount
  useEffect(() => {
    if (!areNotificationsEnabled()) {
      setShowNotifBanner(true);
    }
  }, []);

  const handleEnableNotifications = async () => {
    if (!currentUser) return;
    const result = await requestNotificationPermission(currentUser.uid);
    if (result.success) {
      setShowNotifBanner(false);
      setToast({ open: true, message: '🔔 Push notifications enabled!', severity: 'success' });
    } else {
      setToast({ open: true, message: 'Could not enable notifications: ' + result.error, severity: 'warning' });
    }
  };

  const handleDismissAlert = async (alertId) => {
    if (!familyId) return;
    try {
      await updateDoc(doc(db, 'families', familyId, 'safety_alerts', alertId), {
        reviewed: true,
        reviewedAt: new Date().toISOString()
      });
    } catch (err) {
      console.warn('Error dismissing alert:', err);
    }
  };

  const handleApprovePurchase = async (request) => {
    setConfirmDialog({
      open: true,
      title: 'Approve Purchase?',
      message: `Approve purchase of "${request.name}" ($${request.targetAmount.toFixed(2)}) for ${request.childName}?`,
      onConfirm: async () => {
        try {
          await updateDoc(doc(db, 'savingsGoals', request.id), {
            status: 'approved',
            approvedAt: new Date().toISOString(),
            approvedBy: currentUser.uid
          });
          setPurchaseRequests(prev => prev.filter(r => r.id !== request.id));
          setToast({ open: true, message: `✅ Approved "${request.name}" for ${request.childName}!`, severity: 'success' });
        } catch (err) {
          setToast({ open: true, message: 'Error approving: ' + err.message, severity: 'error' });
        }
      }
    });
  };

  const handleDenyPurchase = async (request) => {
    setConfirmDialog({
      open: true,
      title: 'Deny Purchase?',
      message: `Deny purchase of "${request.name}" for ${request.childName}? Their savings will stay in the goal.`,
      onConfirm: async () => {
        try {
          await updateDoc(doc(db, 'savingsGoals', request.id), {
            status: 'funded',
            requestedAt: null
          });
          setPurchaseRequests(prev => prev.filter(r => r.id !== request.id));
          setToast({ open: true, message: `Purchase request for "${request.name}" denied.`, severity: 'info' });
        } catch (err) {
          setToast({ open: true, message: 'Error denying: ' + err.message, severity: 'error' });
        }
      }
    });
  };

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const uploadProfilePhoto = useCallback(async (file) => {
    if (!file) {
      throw new Error('Please choose an image file.');
    }

    if (!currentUser?.uid) {
      throw new Error('You must be logged in to update your profile photo.');
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('Please upload a valid image file.');
    }

    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image is too large. Choose a file under 5MB.');
    }

    const storageRef = ref(storage, `profile-pics/${currentUser.uid}.jpg`);
    await uploadBytes(storageRef, file);
    const photoURL = await getDownloadURL(storageRef);

    await updateDoc(doc(db, 'users', currentUser.uid), {
      photoURL,
      profilePicUrl: photoURL,
      updatedAt: new Date().toISOString()
    });

    if (!currentUser.isChild) {
      await updateAuthProfile(currentUser, { photoURL });
    }

    await fetchUserProfile(currentUser.uid);
    return photoURL;
  }, [currentUser, fetchUserProfile]);

  const handleProfilePhotoInputChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProfilePicUploading(true);
    try {
      await uploadProfilePhoto(file);
      showToast('Profile photo updated!', 'success');
    } catch (error) {
      console.error('Profile photo upload error:', error);
      showToast(error.message || 'Failed to update profile photo', 'error');
    } finally {
      setProfilePicUploading(false);
      event.target.value = '';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleDeleteChild = async (child) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Family Member?',
      message: `Are you sure you want to remove ${child.displayName} from the family? This will delete their profile and all associated data.`,
      onConfirm: async () => {
        try {
          // Delete child profile first (most important)
          const childId = child.childId || child.id.replace('child:', '');
          const childRef = doc(db, 'families', familyId, 'children', childId);
          await deleteDoc(childRef);

          // Clean up tasks assigned to this child (best-effort, don't block on failure)
          try {
            const childUid = `child:${childId}`;
            const tasksQuery = query(collection(db, 'tasks'), where('assignedTo', 'array-contains', childUid));
            const tasksSnapshot = await getDocs(tasksQuery);
            for (const taskDoc of tasksSnapshot.docs) {
              try { await deleteDoc(taskDoc.ref); } catch (e) { /* skip */ }
            }
          } catch (taskErr) {
            console.warn('Task cleanup failed (non-blocking):', taskErr);
          }

          // Clean up savings goals (best-effort)
          try {
            const childUid = `child:${childId}`;
            const goalsQuery = query(collection(db, 'savingsGoals'), where('userId', '==', childUid));
            const goalsSnapshot = await getDocs(goalsQuery);
            for (const goalDoc of goalsSnapshot.docs) {
              try { await deleteDoc(goalDoc.ref); } catch (e) { /* skip */ }
            }
          } catch (goalErr) {
            console.warn('Goals cleanup failed (non-blocking):', goalErr);
          }

          setToast({ open: true, message: `${child.displayName} has been removed from the family.`, severity: 'success' });
          window.location.reload();
        } catch (error) {
          console.error('Error deleting child:', error);
          setToast({ open: true, message: `Failed to remove child: ${error.message}`, severity: 'error' });
        }
      }
    });
  };

  const handlePayout = async (member, amount) => {
    if (!member || amount <= 0) return;
    
    setPayoutProcessing(true);
    try {
      const childId = member.childId || member.id.replace('child:', '');
      const childRef = doc(db, 'families', familyId, 'children', childId);
      const childDoc = await getDoc(childRef);
      
      if (!childDoc.exists()) {
        alert('Child profile not found');
        return;
      }
      
      const currentAllowance = childDoc.data().allowance || 0;
      const payoutAmt = Math.min(amount, currentAllowance);
      
      if (payoutAmt <= 0) {
        alert(`${member.displayName} has no balance to pay out.`);
        return;
      }
      
      await updateDoc(childRef, {
        allowance: currentAllowance - payoutAmt,
        totalPaidOut: (childDoc.data().totalPaidOut || 0) + payoutAmt,
        lastPaidOut: new Date().toISOString()
      });
      
      setToast({ 
        open: true, 
        message: `✅ Paid out $${payoutAmt.toFixed(2)} to ${member.displayName}`, 
        severity: 'success' 
      });
      setPayoutMember(null);
      setPayoutAmount('');
      
      // Refresh family members
      window.location.reload();
    } catch (error) {
      console.error('Error processing payout:', error);
      alert(`Payout failed: ${error.message}`);
    } finally {
      setPayoutProcessing(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      // Normalize allowance: empty string → 0, string → float
      const taskWithAllowance = {
        ...newTask,
        allowance: newTask.allowance === '' ? 0 : parseFloat(newTask.allowance) || 0
      };
      const createdTask = await createTask(taskWithAllowance);

      // Create screen time rule if enabled and task has start/end times
      if (newTask.screenTimeEnabled && newTask.startTime && newTask.endTime && familyId) {
        const assignedChildren = Array.isArray(newTask.assignedTo) ? newTask.assignedTo : [newTask.assignedTo];
        for (const childId of assignedChildren) {
          if (!childId) continue;
          const dueDate = newTask.dueDate ? new Date(newTask.dueDate) : new Date();
          const [startH, startM] = newTask.startTime.split(':').map(Number);
          const [endH, endM] = newTask.endTime.split(':').map(Number);
          const startDate = new Date(dueDate);
          startDate.setHours(startH, startM, 0, 0);
          const endDate = new Date(dueDate);
          endDate.setHours(endH, endM, 0, 0);

          const rule = buildScreenTimeRule({
            type: 'chore',
            childId,
            startTime: startDate,
            endTime: endDate,
            unlockOnApprove: true,
            createdBy: 'parent',
            linkedChoreId: createdTask?.id || null,
          });

          await addDoc(collection(db, `families/${familyId}/screenTimeRules`), rule);
        }
      }

      setOpenTaskDialog(false);
      setNewTask({
        title: '',
        description: '',
        assignedTo: [],
        allowance: '',
        dueDate: '',
        startTime: '',
        endTime: '',
        photoRequired: 'none',
        isRecurring: false,
        recurringType: 'daily',
        recurringInterval: 1,
        screenTimeEnabled: false,
      });
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleAISuggestChores = async () => {
    try {
      // Get AI suggestions (uses local smart suggestions or Cloud Function)
      const suggestions = await suggestChores(10, 3);
      if (suggestions && suggestions.length > 0) {
        const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
        setNewTask({
          ...newTask,
          title: randomSuggestion.title,
          description: randomSuggestion.description,
          allowance: randomSuggestion.allowance,
          photoRequired: randomSuggestion.photoRequired
        });
      }
    } catch (error) {
      console.error('AI Suggestion error:', error);
      // Fallback to a simple suggestion
      setNewTask({
        ...newTask,
        title: 'Make Your Bed',
        description: 'Straighten sheets and fluff pillows',
        allowance: 1,
        photoRequired: 'after'
      });
    }
  };

  const pendingTasks = tasks.filter(task => 
    task && 
    task.id && 
    task.title && 
    task.title.trim() !== '' && 
    (task.status === 'pending' || task.status === 'pending_approval')
  );
  const completedTasks = tasks.filter(task => 
    task && 
    task.id && 
    task.title && 
    task.title.trim() !== '' && 
    task.status === 'completed'
  );
  const totalAllowance = familyMembers
    .filter(m => m.role === 'child')
    .reduce((sum, member) => sum + (member.allowance || 0), 0);

  // Get user's custom theme and background
  const userTheme = userProfile?.theme;
  const userBackground = userProfile?.backgroundPattern;

  return (
    <Box sx={{ 
      flexGrow: 1, 
      minHeight: '100vh', 
      bgcolor: userBackground?.gradient ? '#f5f5f5' : 'background.default',
      backgroundImage: userBackground?.gradient || 'none',
      backgroundSize: userBackground?.size || 'auto',
      backgroundPosition: userBackground?.position || '0 0',
      backgroundAttachment: 'fixed'
    }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: userTheme?.primary || 'primary.main' }}>
        <Toolbar>
          <Box sx={{ mr: 1, fontSize: 28 }}>🤖</Box>
          <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 1, fontFamily: "'Poppins', sans-serif", overflow: 'hidden', fontSize: { xs: '0.85rem', sm: '1.25rem' } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Earn-E Command Center</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Command Center</Box>
          </Typography>
          <IconButton color="inherit" sx={{ display: { xs: 'none', sm: 'flex' } }} onClick={() => navigate('/calendar')}>
            <CalendarToday />
          </IconButton>
          <IconButton color="inherit" sx={{ display: { xs: 'none', sm: 'flex' } }} onClick={() => navigate('/shopping')}>
            <ShoppingCart />
          </IconButton>
          <IconButton color="inherit" sx={{ display: { xs: 'none', sm: 'flex' } }} onClick={() => navigate('/gallery')}>
            <PhotoLibrary />
          </IconButton>
          <IconButton color="inherit" onClick={() => navigate('/messages')} title="Family Messages">
            <Badge badgeContent={0} color="error">
              <Chat />
            </Badge>
          </IconButton>
          <IconButton color="inherit" sx={{ display: { xs: 'none', sm: 'flex' } }} onClick={() => navigate('/screen-time')} title="Screen Time Rules">
            <PhoneAndroid />
          </IconButton>
          <IconButton color="inherit" onClick={() => setAlertsExpanded(prev => !prev)}>
            <Badge badgeContent={parentAlerts.filter(a => !a.reviewed).length} color="error">
              <Security />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          position: 'fixed',
          top: { xs: 80, sm: 96 },
          right: { xs: 16, sm: 24 },
          display: 'flex',
          gap: 1,
          zIndex: (theme) => theme.zIndex.appBar + 1
        }}
      >
        <IconButton
          component="label"
          aria-label="Upload profile photo"
          sx={{
            bgcolor: 'white',
            boxShadow: 3,
            '&:hover': { bgcolor: 'grey.50' }
          }}
          disabled={profilePicUploading}
        >
          {profilePicUploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <PhotoCamera />
          )}
          <input type="file" hidden accept="image/*" onChange={handleProfilePhotoInputChange} />
        </IconButton>
        <IconButton
          aria-label="Open profile"
          onClick={() => setOpenUserProfile(true)}
          sx={{
            p: 0,
            borderRadius: '50%',
            boxShadow: 4,
            bgcolor: 'transparent'
          }}
        >
          <Avatar
            src={userProfile?.photoURL}
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#FF9800',
              color: 'white',
              border: '3px solid white'
            }}
          >
            {userProfile?.displayName?.charAt(0)}
          </Avatar>
        </IconButton>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Welcome back, {userProfile?.displayName}! 👋
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Here's what's happening in your family today
          </Typography>
        </Box>

        {/* Notification Permission Banner */}
        {showNotifBanner && (
          <Alert
            severity="info"
            icon={<Notifications />}
            sx={{ mb: 2 }}
            action={
              <Box display="flex" gap={1}>
                <Button size="small" color="inherit" variant="outlined" onClick={handleEnableNotifications}>
                  Enable
                </Button>
                <Button size="small" color="inherit" onClick={() => setShowNotifBanner(false)}>
                  Later
                </Button>
              </Box>
            }
          >
            Enable push notifications to get instant alerts about your child's safety
          </Alert>
        )}

        {/* Safety Alerts Section */}
        <Card sx={{ mb: 3, border: '1px solid', borderColor: parentAlerts.some(a => a.severity === 'critical' && !a.reviewed) ? 'error.main' : 'warning.main' }}>
          <CardContent sx={{ pb: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ cursor: 'pointer', mb: alertsExpanded ? 2 : 0 }}
              onClick={() => setAlertsExpanded(prev => !prev)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color={parentAlerts.some(a => a.severity === 'critical' && !a.reviewed) ? 'error' : 'warning'} />
                <Typography variant="h6" fontWeight="bold">
                  Safety Alerts
                </Typography>
                <Badge
                  badgeContent={parentAlerts.filter(a => !a.reviewed).length}
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <Box />
                </Badge>
              </Box>
              <ExpandMore sx={{ transform: alertsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />
            </Box>
            {alertsExpanded && (
              parentAlerts.length > 0 ? (
                <List dense disablePadding>
                  {parentAlerts.map((alert) => (
                    <ListItem
                      key={alert.id}
                      divider
                      sx={{
                        bgcolor: alert.reviewed ? 'transparent' : (alert.severity === 'critical' ? 'error.50' : 'warning.50'),
                        borderRadius: 1,
                        mb: 0.5,
                        opacity: alert.reviewed ? 0.6 : 1
                      }}
                      secondaryAction={
                        !alert.reviewed && (
                          <IconButton edge="end" size="small" onClick={() => handleDismissAlert(alert.id)} aria-label="dismiss alert">
                            <DoneAll fontSize="small" />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: alert.severity === 'critical' ? 'error.main' : 'warning.main', width: 32, height: 32 }}>
                          <Warning sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={alert.severity || 'warning'}
                              size="small"
                              color={alert.severity === 'critical' ? 'error' : 'warning'}
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                            <Typography variant="body2" fontWeight="bold">
                              {alert.childName || 'Unknown'}
                            </Typography>
                            {alert.category && (
                              <Typography variant="caption" color="text.secondary">
                                · {alert.category}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {alert.messageSnippet && (
                              <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                                "{alert.messageSnippet}"
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {alert.timestamp ? new Date(alert.timestamp?.toDate ? alert.timestamp.toDate() : alert.timestamp).toLocaleString() : ''}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    🎉 No safety alerts at this time
                  </Typography>
                </Box>
              )
            )}
          </CardContent>
        </Card>

        {/* Purchase Requests Banner */}
        {purchaseRequests.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: 'warning.light', cursor: 'pointer' }} onClick={() => setOpenPurchaseDialog(true)}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h5">🛒</Typography>
                <Box>
                  <Typography variant="h6" color="warning.dark">
                    {purchaseRequests.length} Purchase Request{purchaseRequests.length > 1 ? 's' : ''} Pending
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    {purchaseRequests.map(r => `${r.childName} wants "${r.name}"`).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => setOpenPendingDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Pending Tasks
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {pendingTasks.length}
                    </Typography>
                  </Box>
                  <Assignment sx={{ fontSize: 40, color: 'warning.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => setOpenCompletedDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Completed Today
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {completedTasks.length}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => setOpenAllowanceDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Owed
                    </Typography>
                    <Typography variant="h4" color={totalAllowance > 0 ? 'warning.main' : 'primary.main'}>
                      ${totalAllowance.toFixed(2)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => setOpenFamilyDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Family Members
                    </Typography>
                    <Typography variant="h4" color="secondary.main">
                      {familyMembers.length}
                    </Typography>
                  </Box>
                  <FamilyRestroom sx={{ fontSize: 40, color: 'secondary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        {activeTab === 'recipes' ? (
          <RecipeManager 
            familyId={familyData?.id}
            onBack={() => setActiveTab('dashboard')}
            addShoppingItem={addShoppingItem}
          />
        ) : (
        <Grid container spacing={3}>
          {/* Invite Code Card */}
          <Grid item xs={12} md={6}>
            <InviteCodeCard familyData={familyData} />
          </Grid>

          {/* Recipe Management */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    🍳 Recipe Collection
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setActiveTab('recipes')}
                  >
                    Manage Recipes
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Build your family's recipe collection. Add recipes from photos or handwritten notes!
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="📸 Photo Upload" size="small" color="primary" />
                  <Chip label="✍️ Handwritten" size="small" color="primary" />
                  <Chip label="🛒 Auto Shopping" size="small" color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Tasks */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Fix #2: Tasks Awaiting Approval - Accordion */}
              <Grid item xs={12}>
            <Accordion defaultExpanded={false}>
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'warning.light',
                  '&:hover': { bgcolor: 'warning.main' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Pending color="warning" />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Tasks Awaiting Approval
                  </Typography>
                  <Chip 
                    label={pendingTasks.length}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {pendingTasks.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No pending tasks! 🎉
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {pendingTasks.map((task) => (
                      <TaskCard key={task.id} task={task} isParent={true} />
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Fix #3: Family Members - Horizontal Avatars */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Family Members
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start'
                }}>
                  {familyMembers.map((member) => (
                    <Box 
                      key={member.id}
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: 1,
                        minWidth: '80px'
                      }}
                    >
                      <Badge
                        badgeContent={`$${member.allowance || 0}`}
                        color="success"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                      >
                        <Avatar 
                          src={member.avatar} 
                          alt={member.displayName}
                          sx={{ 
                            width: 60, 
                            height: 60,
                            border: member.role === 'parent' ? '3px solid' : '2px solid',
                            borderColor: member.role === 'parent' ? 'primary.main' : 'secondary.main',
                            boxShadow: 2
                          }}
                        >
                          {member.displayName?.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '80px' }}>
                          {member.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.role}
                        </Typography>
                      </Box>
                      {member.streak > 0 && (
                        <Chip 
                          label={`🔥 ${member.streak}`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fix #2: Shopping List - Accordion */}
              <Grid item xs={12}>
            <Accordion defaultExpanded={false}>
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'info.light',
                  '&:hover': { bgcolor: 'info.main' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ShoppingCart color="info" />
                    <Typography variant="h6">
                      Shopping List
                    </Typography>
                    <Chip 
                      label={shoppingList.length}
                      color="info"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ShoppingCart />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/shopping');
                    }}
                    sx={{ mr: 2 }}
                  >
                    View Full List
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {shoppingList.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No items in shopping list
                  </Typography>
                ) : (
                  <List>
                    {shoppingList.slice(0, 5).map((item) => (
                      <ListItem 
                        key={item.id} 
                        divider
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate('/shopping')}
                      >
                        <ListItemText
                          primary={item.name}
                          secondary={`Requested by: ${item.requestedBy} • $${item.price || 0}`}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={item.status || 'pending'}
                            color={item.status === 'approved' ? 'success' : 'warning'}
                            size="small"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Smart Affiliate Product Suggestions */}
          <Grid item xs={12}>
            <AffiliateSuggestions 
              userPreferences={userProfile?.preferences || {}}
              familyData={familyData}
            />
          </Grid>

          {/* Calendar Integration - Now handled by native calendar system */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday color="primary" />
                  Calendar Integration
                </Typography>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  Native calendar system active! Tasks automatically sync to your family calendar.
                </Alert>
                <Button 
                  variant="contained" 
                  startIcon={<CalendarToday />}
                  onClick={() => navigate('/calendar')}
                  sx={{ mb: 2 }}
                >
                  View Family Calendar
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Your new native calendar provides drag-and-drop scheduling, recurring events, 
                  and advanced filtering - all integrated with your family tasks.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* PWA Settings */}
          <Grid item xs={12}>
            <PWASettings />
          </Grid>

          {/* Fix #2: Completed Tasks History - Accordion */}
          <Grid item xs={12}>
            <Accordion defaultExpanded={false}>
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'success.light',
                  '&:hover': { bgcolor: 'success.main' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <CheckCircle color="success" />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Completed Tasks History
                  </Typography>
                  <Chip 
                    label={completedTasks.length}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <CompletedTasksCard 
                  tasks={tasks} 
                  familyMembers={familyMembers}
                  isParent={true}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
            </Grid>
          </Grid>
        </Grid>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add task"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #4CAF50, #81C784)',
            '&:hover': {
              background: 'linear-gradient(45deg, #388E3C, #4CAF50)',
            }
          }}
          onClick={() => setOpenTaskDialog(true)}
        >
          <Add />
        </Fab>

        {/* Create Task Dialog */}
        <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Create New Task
              <Button
                startIcon={<AutoAwesome />}
                onClick={handleAISuggestChores}
                size="small"
                variant="outlined"
                color="secondary"
              >
                AI Suggest
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Task Title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                fullWidth
              />
              <TextField
                label="Description"
                multiline
                rows={3}
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Assign To (Children, Parents, or Spouse)</InputLabel>
                <Select
                  multiple
                  value={newTask.assignedTo}
                  onChange={(e) => setNewTask({ ...newTask, assignedTo: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const member = familyMembers.find(m => m.id === value);
                        return (
                          <Chip key={value} label={member?.displayName} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {familyMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.displayName} ({member.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Allowance Amount ($)"
                type="number"
                value={newTask.allowance}
                onChange={(e) => setNewTask({ ...newTask, allowance: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                placeholder="0.00"
                fullWidth
                helperText="Enter dollar amount (e.g., 1.50 = $1.50)"
              />
              <TextField
                label="Due Date"
                type="date"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start Time (optional)"
                  type="time"
                  value={newTask.startTime}
                  onChange={(e) => setNewTask({ ...newTask, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="When to start the chore"
                />
                <TextField
                  label="Due By Time (optional)"
                  type="time"
                  value={newTask.endTime}
                  onChange={(e) => setNewTask({ ...newTask, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="When it needs to be done"
                />
              </Box>
              <FormControl fullWidth>
                <InputLabel>Photo Requirement</InputLabel>
                <Select
                  value={newTask.photoRequired}
                  onChange={(e) => setNewTask({ ...newTask, photoRequired: e.target.value })}
                >
                  <MenuItem value="none">No Photo Required</MenuItem>
                  <MenuItem value="after">After Photo Required</MenuItem>
                  <MenuItem value="both">Before & After Photos Required</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              {/* Recurring Task Options */}
              <FormControlLabel
                control={
                  <Switch
                    checked={newTask.isRecurring}
                    onChange={(e) => setNewTask({ ...newTask, isRecurring: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      🔄 Make this a recurring task
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Task will automatically renew based on schedule
                    </Typography>
                  </Box>
                }
              />

              {newTask.isRecurring && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Recurring Schedule</InputLabel>
                    <Select
                      value={newTask.recurringType}
                      onChange={(e) => setNewTask({ ...newTask, recurringType: e.target.value })}
                    >
                      <MenuItem value="daily">⏰ Daily</MenuItem>
                      <MenuItem value="weekdays">📅 Weekdays (Mon-Fri)</MenuItem>
                      <MenuItem value="weekends">🎉 Weekends (Sat-Sun)</MenuItem>
                      <MenuItem value="weekly">📆 Weekly (Every 7 days)</MenuItem>
                      <MenuItem value="biweekly">📋 Bi-Weekly (Every 14 days)</MenuItem>
                      <MenuItem value="monthly">🗓️ Monthly (Every 30 days)</MenuItem>
                      <MenuItem value="custom">⚙️ Custom Interval</MenuItem>
                    </Select>
                  </FormControl>

                  {newTask.recurringType === 'custom' && (
                    <TextField
                      label="Repeat Every (days)"
                      type="number"
                      value={newTask.recurringInterval}
                      onChange={(e) => setNewTask({ ...newTask, recurringInterval: parseInt(e.target.value) || 1 })}
                      fullWidth
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Enter number of days between repeats (1-365)"
                    />
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    💡 Recurring tasks will automatically reset after completion
                  </Typography>
                </Box>
              )}
            {/* Screen Time Lock Option */}
              <Divider sx={{ my: 2 }} />
              <Tooltip
                title={
                  !newTask.startTime || !newTask.endTime
                    ? "Screen time rules require a scheduled time. Add start and end times to enable."
                    : "During this chore, device locks to Earn-E app. Child can still use Earn-E features but cannot access other apps. Device unlocks when you approve the chore."
                }
                placement="top"
                arrow
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newTask.screenTimeEnabled}
                        onChange={(e) => setNewTask({ ...newTask, screenTimeEnabled: e.target.checked })}
                        color="warning"
                        disabled={!newTask.startTime || !newTask.endTime}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          📱 Apply screen time rules during this chore
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Locks device to Earn-E app. Unlocks when you approve.
                        </Typography>
                      </Box>
                    }
                  />
                </span>
              </Tooltip>
              {newTask.screenTimeEnabled && (
                <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                  <Typography variant="caption">
                    📌 <strong>Android only.</strong> Screen time rules only work on Android devices with the Earn-E app installed.
                  </Typography>
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} variant="contained">Create Task</Button>
          </DialogActions>
        </Dialog>

        {/* Family Members Dialog */}
        <Dialog 
          open={openFamilyDialog} 
          onClose={() => setOpenFamilyDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <FamilyRestroom color="secondary" />
              Family Members Overview
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {familyMembers.map((member) => {
                const memberTasks = tasks.filter(t => t.assignedTo?.includes(member.id));
                const memberCompleted = memberTasks.filter(t => t.status === 'completed').length;
                const memberPending = memberTasks.filter(t => t.status === 'pending').length;
                
                return (
                  <Grid item xs={12} sm={6} key={member.id}>
                    <Card 
                      sx={{ 
                        bgcolor: member.role === 'parent' ? 'primary.light' : 'secondary.light',
                        color: 'white'
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
                            {member.displayName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="h6">{member.displayName}</Typography>
                            <Chip 
                              label={member.role} 
                              size="small" 
                              sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
                            />
                          </Box>
                        </Box>
                        {member.role === 'child' && (
                          <>
                            <Box>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                💰 Allowance: ${(member.allowance || 0).toFixed(2)}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                ✅ Completed: {memberCompleted} tasks
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                ⏳ Pending: {memberPending} tasks
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                💎 Total Earned: ${(member.totalEarned || 0).toFixed(2)}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteChild(member)}
                              sx={{ 
                                mt: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderColor: 'rgba(255,255,255,0.5)',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'rgba(255,255,255,0.3)',
                                  borderColor: 'white'
                                }
                              }}
                            >
                              Remove Child
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFamilyDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Allowance Breakdown Dialog */}
        <Dialog 
          open={openAllowanceDialog} 
          onClose={() => { setOpenAllowanceDialog(false); setPayoutMember(null); setPayoutAmount(''); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUp color="primary" />
              Allowance Owed
            </Box>
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 1 }}>
              {familyMembers.filter(m => m.role === 'child').map((member) => (
                <React.Fragment key={member.id}>
                  <ListItem divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {member.displayName?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.displayName}
                      secondary={
                        <>
                          Total Earned: ${(member.totalEarned || 0).toFixed(2)}
                          {member.totalPaidOut > 0 && ` · Paid Out: $${(member.totalPaidOut || 0).toFixed(2)}`}
                        </>
                      }
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" color={(member.allowance || 0) > 0 ? 'warning.main' : 'success.main'}>
                        ${(member.allowance || 0).toFixed(2)}
                      </Typography>
                      {(member.allowance || 0) > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => {
                            setPayoutMember(member);
                            setPayoutAmount((member.allowance || 0).toFixed(2));
                          }}
                        >
                          Pay
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                  {payoutMember?.id === member.id && (
                    <ListItem sx={{ bgcolor: 'action.hover', flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Pay out to {member.displayName} (owed: ${(member.allowance || 0).toFixed(2)})
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          label="Amount"
                          value={payoutAmount}
                          onChange={(e) => setPayoutAmount(e.target.value)}
                          inputProps={{ min: 0.01, max: member.allowance || 0, step: 0.01 }}
                          sx={{ width: 120 }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={payoutProcessing || !payoutAmount || parseFloat(payoutAmount) <= 0}
                          onClick={() => handlePayout(member, parseFloat(payoutAmount))}
                        >
                          {payoutProcessing ? 'Processing...' : `Pay $${parseFloat(payoutAmount || 0).toFixed(2)}`}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => { setPayoutMember(null); setPayoutAmount(''); }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </ListItem>
                  )}
                </React.Fragment>
              ))}
              <ListItem>
                <ListItemText
                  primary={<Typography variant="h6">Total Owed</Typography>}
                />
                <Typography variant="h5" color={totalAllowance > 0 ? 'warning.main' : 'success.main'} fontWeight="bold">
                  ${totalAllowance.toFixed(2)}
                </Typography>
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpenAllowanceDialog(false); setPayoutMember(null); setPayoutAmount(''); }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Purchase Requests Dialog */}
        <Dialog
          open={openPurchaseDialog}
          onClose={() => setOpenPurchaseDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              🛒 Purchase Requests
            </Box>
          </DialogTitle>
          <DialogContent>
            {purchaseRequests.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>No pending purchase requests.</Typography>
            ) : (
              <List>
                {purchaseRequests.map((req) => (
                  <ListItem key={req.id} divider sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {req.imageUrl && (
                        <Avatar src={req.imageUrl} variant="rounded" sx={{ width: 56, height: 56 }} />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="bold">{req.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {req.childName} · Saved ${(req.currentAmount || 0).toFixed(2)} of ${(req.targetAmount || 0).toFixed(2)}
                        </Typography>
                        {req.amazonUrl && (
                          <Typography variant="body2">
                            <a href={req.amazonUrl} target="_blank" rel="noopener noreferrer">View Product →</a>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleDenyPurchase(req)}>
                        Deny
                      </Button>
                      <Button variant="contained" color="success" size="small" onClick={() => handleApprovePurchase(req)}>
                        Approve Purchase
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPurchaseDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Pending Tasks Dialog */}
        <Dialog 
          open={openPendingDialog} 
          onClose={() => setOpenPendingDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Pending color="warning" />
              Pending Tasks ({pendingTasks.length})
            </Box>
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 1 }}>
              {pendingTasks.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={2}>
                  No pending tasks
                </Typography>
              ) : (
                pendingTasks.map((task) => {
                  const assignedMembers = familyMembers.filter(m => 
                    task.assignedTo?.includes(m.id)
                  );
                  return (
                    <ListItem key={task.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <Pending />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Assigned to: {assignedMembers.map(m => m.displayName).join(', ')}
                            </Typography>
                            <Typography variant="body2">
                              Due: {task.dueDate || 'No due date'}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={`$${(task.allowance || 0).toFixed(2)}`}
                        color="primary"
                        size="small"
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPendingDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Completed Tasks Dialog */}
        <Dialog 
          open={openCompletedDialog} 
          onClose={() => setOpenCompletedDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" />
              Completed Tasks ({completedTasks.length})
            </Box>
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 1 }}>
              {completedTasks.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={2}>
                  No completed tasks yet
                </Typography>
              ) : (
                completedTasks.map((task) => {
                  const assignedMembers = familyMembers.filter(m => 
                    task.assignedTo?.includes(m.id)
                  );
                  return (
                    <ListItem key={task.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.main' }}>
                          <CheckCircle />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Completed by: {assignedMembers.map(m => m.displayName).join(', ')}
                            </Typography>
                            <Typography variant="body2">
                              {task.approvedAt ? `Approved: ${new Date(task.approvedAt).toLocaleDateString()}` : 'Approved'}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={`$${(task.allowance || 0).toFixed(2)}`}
                        color="success"
                        size="small"
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompletedDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Widget */}
        <FeedbackWidget />

        {/* Review Prompt */}
        <ReviewPrompt />

        {/* Phase 1: Floating Burt Chat Widget */}
        <BurtChatWidget
          tasks={tasks}
          completedTasks={completedTasks}
          familyMembers={familyMembers}
          shoppingList={shoppingList}
          familyData={familyData}
          currentUser={{ uid: userProfile?.uid }}
          addShoppingItem={addShoppingItem}
        />
      </Container>

      <UserProfile
        open={openUserProfile}
        onClose={() => setOpenUserProfile(false)}
        userProfile={userProfile}
        onUploadProfilePhoto={uploadProfilePhoto}
        onShowToast={showToast}
        onLogout={handleLogout}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Confirm Dialog — replaces window.confirm for Play Store TWA compatibility */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            sx={{ minHeight: 48 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmDialog(prev => ({ ...prev, open: false }));
              if (confirmDialog.onConfirm) confirmDialog.onConfirm();
            }}
            sx={{ minHeight: 48 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ParentDashboard;
